import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Messages } from 'primereact/messages'

import { usePanelContext } from '../../../contexts'
import { useToastContext } from '../../../contexts/toast'
import { useAllEmailSettings } from '../../../data/settings'
import { requestUpdateEmailSettings } from '../../../data/settings/api'
import { getHandleableError } from '../../../utils/errors/isXiaError'
import { emailRegex } from '../../../utils/regex'
import { useCurrentTenantConfig } from '../../../utils/tenants'
import { useSupplierById } from '../../selfservice/supplier/data'
import { EmailForm } from './EmailForm'

interface EmailDto {
  inbound?: string
  outbound?: string
  support?: string
}
interface FormValues extends EmailDto {}
export type FormFieldName = keyof EmailDto

export const EmailFormContainer: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [wrongInboundEmails, setWrongInboundEmails] = useState<string[] | null>(
    null,
  )
  const [wrongOutboundEmails, setWrongOutboundEmails] = useState<
    string[] | null
  >(null)
  const [wrongSupportEmails, setWrongSupportEmails] = useState<string[] | null>(
    null,
  )
  const messagesRef = useRef<Messages>(null)

  const panelId = 'settings-mail'
  const { isEdit } = usePanelContext()

  const { data, isLoading, mutate } = useAllEmailSettings()

  const { projectId } = useCurrentTenantConfig()

  const { data: supplier } = useSupplierById(projectId as string)

  const separator = ';'

  const validateEmailField = (
    data: string,
    fieldErrors: FormikErrors<FormValues>,
    fieldName: FormFieldName,
    setWrongEmails: Function,
  ) => {
    const emailValidationRegex = emailRegex
    const emails = data?.split(separator)
    const invalidEmails = emails?.filter(
      email => !emailValidationRegex.test(email),
    )
    if (invalidEmails?.length) {
      setWrongEmails(invalidEmails)
      fieldErrors[fieldName] = t(
        'settings.email.common.errorMessage.wrongEmail',
        { count: invalidEmails?.length },
      )
    } else {
      setWrongEmails(null)
    }
  }

  useEffect(() => {
    if (messagesRef.current) {
      if (isEdit(panelId)) {
        messagesRef.current.clear()
        messagesRef.current.show({
          id: '1',
          sticky: true,
          severity: 'info',
          detail: t('settings.email.notifications.message'),
          closable: false,
        })
      } else {
        messagesRef.current.clear()
      }
    }
  }, [isEdit, t])

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      inbound: data?.Inbound,
      outbound: data?.Outbound,
      support: data?.Support,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      if (data.inbound)
        validateEmailField(
          data.inbound,
          errors,
          'inbound',
          setWrongInboundEmails,
        )

      if (data.outbound)
        validateEmailField(
          data.outbound,
          errors,
          'outbound',
          setWrongOutboundEmails,
        )

      if (data.support)
        validateEmailField(
          data.support,
          errors,
          'support',
          setWrongSupportEmails,
        )

      return errors
    },
    onSubmit: async (formData: FormValues) => {
      try {
        setIsRequestRunning(true)

        await handleSubmit(formData)
        mutate()

        // show success toast
        showToast({
          severity: 'success',
          summary: t('settings.email.toasts.successToastSummary'),
          detail: t('settings.email.toasts.successToastDetail'),
          life: 3000,
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const handleSubmit = async (formData: FormValues) => {
    const emailSettings = produce(data, draft => {
      if (draft) {
        draft.Inbound = formData.inbound
        draft.Outbound = formData.outbound
        draft.Support = formData.support
      }
    })

    if (emailSettings) await requestUpdateEmailSettings(emailSettings)
  }

  const isFormFieldInvalid = (name: FormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <EmailForm
      formik={formik}
      panelId={panelId}
      separator={separator}
      messagesRef={messagesRef}
      isFormFieldInvalid={isFormFieldInvalid}
      wrongInboundEmails={wrongInboundEmails}
      wrongOutboundEmails={wrongOutboundEmails}
      wrongSupportEmails={wrongSupportEmails}
      getFormErrorMessage={getFormErrorMessage}
      isEdit={isEdit}
      isLoading={isLoading}
      isRequestRunning={isRequestRunning}
      hasChangesPending={supplier?.HasChangesPending}
    />
  )
}
